
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ArrowLeft, ArrowRight } from "lucide-react";
import NoData from "./errlogo.png";

const Table = ({
  columns,
  data,
  rowsPerPage = 10,
  tableClassName = "table",
  tableContainerClassName = "table-responsive",
  rowClassName = "",
  headerClassName = "",
  cellClassName = "",
  renderCustomHeader,
  renderCustomCell,
  page,
  isPage = true,
  search
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [perPage, setPerPage] = useState(rowsPerPage);

  const pageCount = Math.ceil(data.length / perPage);
  const startRowIndex = isPage ? currentPage * perPage : (page - 1) * perPage;
  const currentPageData = isPage
    ? data.slice(startRowIndex, startRowIndex + perPage)
    : data;

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isPage && page !== undefined) {
      setCurrentPage(page - 1);
    }
  }, [page, isPage]);

  const handlePageChange = (direction) => {
    setCurrentPage((prev) => {
      const next = prev + direction;
      return Math.max(0, Math.min(next, pageCount - 1));
    });
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [search, perPage]);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "300px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="">

      <div
        className={tableContainerClassName}

      >

        <table
          className={tableClassName}
          style={{
            width: "100%",

            borderCollapse: "separate",
            borderSpacing: "0",
          }}
        >
          <thead
            style={{
              background: "#231788",
              position: "sticky",
              top: 0,
              zIndex: 2,
            }}
          >
            <tr>
              <th
                style={{
                  padding: "14px",
                  fontWeight: 600,
                  background: "#3529a1",
                  color: "#ffffff",
                  borderBottom: "none",
                }}
              >
                S.no
              </th>

              {columns.map((col, idx) => (
                <th
                  key={idx}
                  style={{
                    padding: "14px",
                    fontWeight: 600,
                    background: "#3529a1",
                    color: "#ffffff",
                    borderBottom: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {renderCustomHeader ? renderCustomHeader(col) : col.Header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.length === 0 && (
              <tr>
                <td colSpan="100%" className="text-center py-5">
                  {/* <div>
          <img src={NoData} style={{ width: "250px" }} />
          <h4 className="text-muted mt-3">No Data Available</h4>
        </div> */}
                  <div style={{ textAlign: "center", overflow: "hidden" }}>
                    <img src={NoData} className="nodata-combo" style={{ width: "250px" }} />
                    <h4 className="text-muted mt-3">No Data Available</h4>
                  </div>
                </td>
              </tr>
            )}

            {currentPageData.map((row, rowIndex) => (
              <tr

                key={rowIndex + startRowIndex}
                style={{
                  borderBottom: "1px solid #e5e7eb",
                  transition: "0.2s",
                  background: rowIndex % 2 === 0 ? "#9e7676" : "#98a4bd", // Zebra row
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#f9fafb")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <td style={{ padding: "12px", fontWeight: 400 }}>
                  {rowIndex + 1 + startRowIndex}
                </td>

                {columns.map((col, colIndex) => {
                  const value = row[col.accessor];
                  return (
                    <td key={colIndex} style={{ padding: "9px" }}>
                      {col.Cell
                        ? col.Cell({ cell: { value, row } })
                        : renderCustomCell
                          ? renderCustomCell(value, row)
                          : value}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>


      {isPage && data.length > 0 && (
        <div className="d-flex align-items-center justify-content-between mt-3 px-2 pb-2 flex-wrap flex-sm-nowrap gap-2" style={{ fontSize: "13px" }}>
          <div className="d-flex align-items-center gap-2">
            <label htmlFor="rowsPerPage" className="mb-0 text-nowrap">
              Rows:
            </label>
            <select
              id="rowsPerPage"
              className="form-select form-select-sm"
              style={{ width: "65px", padding: "0.25rem 0.5rem" }}
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
            >
              {[5, 10, 20, 50, 100].map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>
          </div>

          <div className="pagination d-flex align-items-center gap-2 m-0">
            <button
              onClick={() => handlePageChange(-1)}
              disabled={currentPage === 0}
              className="btn btn-outline-primary btn-sm px-2 py-1 d-flex align-items-center"
            >
              <ArrowLeft size={16} />
            </button>
            <span className="text-nowrap">
              Page {currentPage + 1} of {pageCount}
            </span>
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === pageCount - 1}
              className="btn btn-outline-primary btn-sm px-2 py-1 d-flex align-items-center"
            >
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
      Cell: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  rowsPerPage: PropTypes.number,
  tableClassName: PropTypes.string,
  tableContainerClassName: PropTypes.string,
  rowClassName: PropTypes.string,
  headerClassName: PropTypes.string,
  cellClassName: PropTypes.string,
  renderCustomHeader: PropTypes.func,
  renderCustomCell: PropTypes.func,
  page: PropTypes.number,
  isPage: PropTypes.bool,
};

export default Table;
